/**
 * IntegrationPage.jsx
 * ==================
 * Integration management page for connecting external services
 * 
 * Created: 2025-01-27
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelectedAppStore } from '../../store/useSelectedAppStore';
import { ProfileIcon, ProfileMenu, ProfileMenuItem } from '../Header/ProfileMenu';
import { FaUserCircle } from 'react-icons/fa';
import { IoSettingsOutline } from 'react-icons/io5';
import { FiLogOut } from 'react-icons/fi';
import Logout from '../Logout/Logut';
import useProfileStore from '../../store/useProfileStore';
import logo from '../../assets/images/newLogo.png';
import './IntegrationPage.css';

const IntegrationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedApp } = useSelectedAppStore();
  const userImage = useProfileStore((state) => state.userImage);
  const appName = searchParams.get('app') || selectedApp?.appName || selectedApp?.name;
  const [openAccordion, setOpenAccordion] = useState(null);

  const integrations = [
    {
      id: 'payment',
      title: '💳 Payment Gateway',
      description: 'Integrate payment processing for your application',
      details: 'Connect with payment providers like Stripe, PayPal, or Razorpay to accept payments from your users.',
      comingSoon: true
    },
    {
      id: 'email',
      title: '📧 Email Service',
      description: 'Send emails and notifications',
      details: 'Configure email services like SendGrid, Mailgun, or SMTP to send transactional emails and notifications.',
      comingSoon: true
    },
    {
      id: 'sms',
      title: '📱 SMS Service',
      description: 'Send SMS notifications',
      details: 'Integrate SMS providers like Twilio or AWS SNS to send text messages to your users.',
      comingSoon: true
    },
    {
      id: 'whatsapp',
      title: '💬 WhatsApp Integration',
      description: 'Connect WhatsApp for messaging',
      details: 'Add WhatsApp messaging functionality to communicate with your users directly.',
      comingSoon: true
    },
    {
      id: 'social',
      title: '📱 Social Media',
      description: 'Connect social media accounts',
      details: 'Link your social media profiles and enable social login or sharing features.',
      comingSoon: true
    },
    {
      id: 'api',
      title: '🔌 API Integration',
      description: 'Connect external APIs',
      details: 'Integrate with third-party APIs and webhooks to extend your application functionality.',
      comingSoon: true
    },
    {
      id: 'storage',
      title: '☁️ Cloud Storage',
      description: 'Connect cloud storage services',
      details: 'Integrate with cloud storage providers like AWS S3, Google Cloud Storage, or Azure Blob Storage.',
      comingSoon: true
    },
    {
      id: 'analytics',
      title: '📊 Analytics Services',
      description: 'Connect analytics platforms',
      details: 'Integrate with analytics services like Google Analytics, Mixpanel, or Amplitude to track user behavior.',
      comingSoon: true
    }
  ];

  const toggleAccordion = (id) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  return (
    <div className="integration-page">
      {/* Custom Navigation Bar - Similar to AnalyticsPage */}
      <div style={{
        padding: '12px 24px',
        background: '#050618',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        minHeight: '5.25rem',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        {/* Left Section: MUDUMBAI Logo */}
        <div 
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            marginRight: '32px'
          }}
          onClick={() => navigate('/')}
        >
          <img 
            src={logo} 
            alt="MUDUMBAI Logo" 
            style={{
              height: '3.5rem',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </div>

        {/* Center Section: Page-specific content */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flex: 1
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              🔌
            </div>
            <div>
              <h1 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '700',
                color: '#FFFFFF',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
              }}>
                Integrations
              </h1>
              <p style={{
                margin: 0,
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.8)'
              }}>
                {appName ? `Integrations for: ${appName}` : 'Connect external services and APIs'}
              </p>
            </div>
          </div>
        </div>

        {/* Right Section: Go to Dashboard and Profile */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          {/* Go to Dashboard Button */}
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '8px 14px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.3)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            }}
          >
            <span>🏠</span>
            <span>Go to Dashboard</span>
          </button>

          {/* Profile Icon */}
          <div style={{ marginLeft: '12px' }}>
            <ProfileMenu
              trigger={
                <div style={{
                  width: '2.75rem',
                  height: '2.75rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                }}
                >
                  <ProfileIcon userImage={userImage} />
                </div>
              }
            >
              <ProfileMenuItem onClick={() => navigate('/userprofile')}>
                <FaUserCircle size={22} /> Profile
              </ProfileMenuItem>
              <ProfileMenuItem onClick={() => navigate('/settings')}>
                <IoSettingsOutline size={22} /> Setting
              </ProfileMenuItem>
              <ProfileMenuItem>
                <FiLogOut size={20} /> <Logout />
              </ProfileMenuItem>
            </ProfileMenu>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="integration-content">
        <div className="integration-header">
          <h2 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#111827',
            marginBottom: '12px'
          }}>
            🔗 Manage Integrations
          </h2>
          <p style={{
            fontSize: '16px',
            color: '#6B7280',
            marginBottom: '8px'
          }}>
            Connect your application with external services, APIs, and third-party tools to extend functionality.
          </p>
          {appName && (
            <p style={{
              fontSize: '14px',
              color: '#9CA3AF',
              margin: 0
            }}>
              Currently managing integrations for: <strong>{appName}</strong>
            </p>
          )}
        </div>

        <div className="integration-accordion">
          {integrations.map((integration) => (
            <div key={integration.id} className="integration-accordion-item">
              <button
                className={`integration-accordion-header ${openAccordion === integration.id ? 'open' : ''}`}
                onClick={() => toggleAccordion(integration.id)}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                  <span style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                    {integration.title}
                  </span>
                  <span style={{ fontSize: '14px', color: '#6B7280' }}>
                    {integration.description}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {integration.comingSoon && (
                    <span style={{
                      fontSize: '12px',
                      padding: '4px 8px',
                      background: '#FEF3C7',
                      color: '#92400E',
                      borderRadius: '4px',
                      fontWeight: '500'
                    }}>
                      Coming Soon
                    </span>
                  )}
                  <span style={{
                    fontSize: '20px',
                    color: '#6B7280',
                    transition: 'transform 0.2s',
                    transform: openAccordion === integration.id ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}>
                    ▼
                  </span>
                </div>
              </button>
              {openAccordion === integration.id && (
                <div className="integration-accordion-content">
                  <div style={{ padding: '24px' }}>
                    <p style={{
                      fontSize: '14px',
                      color: '#6B7280',
                      marginBottom: '16px',
                      lineHeight: '1.6'
                    }}>
                      {integration.details}
                    </p>
                    {integration.comingSoon && (
                      <div style={{
                        padding: '12px',
                        background: '#EFF6FF',
                        borderRadius: '6px',
                        border: '1px solid #BFDBFE'
                      }}>
                        <p style={{
                          fontSize: '13px',
                          color: '#1E40AF',
                          margin: 0,
                          fontWeight: '500'
                        }}>
                          💡 This integration will be available soon. Check back later for updates.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IntegrationPage;
